import { useState, useEffect, useContext, useCallback } from "react"
import { reservationStepPropTypes } from "source/registrations/propTypes"
import { useParams } from "react-router-dom"
import { find as _find, isEmpty } from "lodash"
import { isStringBlank } from "source/registrations/utils/helpers"
import { ReservationContext } from "source/registrations/reservations/ReservationContext"
import { patchPaymentIntent } from "source/registrations/api/payments"
import { Loading } from "source/shared/components"
import { useEvent } from "source/registrations/hooks/useEvent"
import SectionLayout from "source/registrations/reservations/SectionLayout"
import StripeWrapper from "source/registrations/StripeWrapper"
import StripeForm from "source/registrations/StripeForm"
import AttendeesBreakdown from "./AttendeesBreakdown"
import ReviewPaymentDiscountForm from "./ReviewPaymentDiscountForm"
import ReviewPaymentMethods from "./ReviewPaymentMethods"
import ReviewPaymentType from "./ReviewPaymentType"
import PaymentProcessingProgressBar from "../PaymentProcessingProgressBar"
import classNames from "classnames"
import { useOrganizationFlag as useOrganizationFlagFromPayments } from "source/payments/hooks/useOrganization"
import { refreshPaymentIntent } from "source/registrations/api/payments"
import { object } from "prop-types"
import moment from "moment-timezone"
import { useAllowedPaymentMethodTypes } from "source/registrations/hooks/useAllowedPaymentMethodTypes.js"

const ReviewPaymentWrapper = (props) => {
  const { eventId, reservationUuid } = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [paymentIntent, setPaymentIntent] = useState(null)

  useEffect(() => {
    const loadPaymentIntent = async () => {
      const refreshedPaymentIntent = await refreshPaymentIntent(
        eventId,
        reservationUuid,
      )
      if (
        refreshedPaymentIntent?.data &&
        !isEmpty(refreshedPaymentIntent.data)
      ) {
        setPaymentIntent(refreshedPaymentIntent.data)
      }
    }
    setIsLoading(true)
    loadPaymentIntent()
    setIsLoading(false)
  }, [eventId, reservationUuid])

  useEffect(() => {
    if (isEmpty(paymentIntent) && !isEmpty(props.reservation?.paymentIntent)) {
      setPaymentIntent(props.reservation.paymentIntent)
    }
  }, [paymentIntent, props.reservation?.paymentIntent])

  if (isLoading) {
    return <Loading />
  }

  return (
    <ReviewPayment
      {...props}
      paymentIntent={paymentIntent || props.reservation.paymentIntent}
    />
  )
}

ReviewPaymentWrapper.propTypes = {
  reservation: object.isRequired,
}

const ReviewPayment = ({
  attendees,
  addOns,
  organization,
  reservation,
  isProcessing,
  isAddOnsEnabled,
  onIsProcessing,
  completeRegistrationId,
  onReservationSave,
  className,
  headingId,
  error: reservationError,
  onPaymentComplete,
  step,
  paymentIntent: paymentIntentFromProps,
  disablePaymentForm,
}) => {
  const { eventId } = useParams()
  const eventResponse = useEvent(eventId)

  const {
    data: {
      currency,
      allowCreditCardPayments,
      allowOfflinePayments,
      allowAchPayments,
      discounts,
    },
  } = eventResponse

  const { stripe } = useContext(ReservationContext)
  const allowedPaymentMethods = useAllowedPaymentMethodTypes(eventResponse.data)

  const savedPaymentsEnabled = useOrganizationFlagFromPayments(
    "useSavedPaymentMethods",
  )

  const showSavedPaymentMethods = savedPaymentsEnabled

  const {
    paymentIntegration: { isConnected, isTestMode },
  } = organization

  const [error, setError] = useState(reservationError)
  const [showDiscounts, setShowDiscounts] = useState(false)
  const [amount, setAmount] = useState(0)
  const [paymentType, setPaymentType] = useState("full")
  const [paymentMethod, setPaymentMethod] = useState("cashCheck")
  const [discountError, setDiscountError] = useState(null)
  const [discountCode, setDiscountCode] = useState("")
  const [canMakePayment, setCanMakePayment] = useState(!isProcessing)
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)
  const [paymentIntentError, setPaymentIntentError] = useState(null)
  const [paymentIntent, setPaymentIntent] = useState(paymentIntentFromProps)
  const [isCaptureProcessing, setIsCaptureProcessing] = useState(false)
  const [reducedOtherAmount, setReducedOtherAmount] = useState(null)
  const [showStripeForm, setShowStripeForm] = useState(true)

  useEffect(() => {
    if (!isEmpty(paymentIntentFromProps)) {
      setPaymentIntent(paymentIntentFromProps)
    }
  }, [paymentIntentFromProps])

  useEffect(() => {
    setAmount(reservation.totalDue)
  }, [reservation.totalDue])

  const handlePaymentIntentUpdate = (paymentIntent) => {
    setPaymentIntent(paymentIntent)
  }

  const hasTotalDue = reservation.totalDue > 0

  const allowCreditCard =
    hasTotalDue && (allowCreditCardPayments || allowAchPayments)

  const useCreditCardForm =
    allowCreditCard && paymentMethod === "creditCard" && paymentType !== "later"

  const usePaymentMethods = allowOfflinePayments && allowCreditCard

  const usePaymentTypes =
    hasTotalDue &&
    paymentMethod === "creditCard" &&
    reservation.minimumDue >= 0 &&
    reservation.minimumDue !== reservation.totalDue

  const updatePaymentIntent = useCallback(
    async (newAmount) => {
      if (isStringBlank(newAmount) || newAmount === 0) return

      setIsPaymentProcessing(true)

      const response = await patchPaymentIntent(
        reservation.eventId,
        reservation.id,
        {
          data: { attributes: { amount: newAmount } },
          fields: {
            PaymentIntent:
              "amount,amount_formatted,client_secret,customer,currency,customer_session_client_secret,payment_method,payment_method_types,status",
          },
        },
      )

      if (response.errors) {
        setPaymentIntentError(response.errors[0]?.detail)
      } else {
        setPaymentIntentError(null)
        setPaymentIntent({
          ...paymentIntent,
          ...response.data,
        })
      }

      setIsPaymentProcessing(false)
    },
    [reservation.eventId, reservation.id, paymentIntent],
  )

  useEffect(() => {
    setError(reservationError)
  }, [reservationError])

  useEffect(() => {
    setPaymentMethod(allowCreditCard ? "creditCard" : "cashCheck")
  }, [allowCreditCard])

  useEffect(() => {
    if (isPaymentProcessing) {
      setCanMakePayment(false)
    } else if (paymentIntent?.amount !== amount) {
      if (
        typeof amount === "number" &&
        typeof paymentIntent?.amount === "number"
      ) {
        updatePaymentIntent(amount)
      }
      setCanMakePayment(false)
    } else {
      setCanMakePayment(true)
    }
  }, [isPaymentProcessing, paymentIntent?.amount, amount, updatePaymentIntent])

  // Debugging purposes only
  // https://app.bugsnag.com/planning-center/church-center-web/errors/633f56cca5b98d0008f1b3ae?filters[event.since]=30d&filters[error.status]=open&filters[error.assigned_to]=me
  // "We could not retrieve data from the specified Element. Please make sure the Element you are attempting to use is still mounted."
  useEffect(() => {
    if (
      isConnected &&
      useCreditCardForm &&
      (!stripe || !paymentIntent?.clientSecret)
    ) {
      return () => {
        console.error(
          `something went wrong!\n
            Stripe: ${JSON.stringify(stripe)} \n
            PaymentIntent: ${JSON.stringify(paymentIntent)} \n
            isConnected: ${isConnected} \n
            useCreditCardForm: ${useCreditCardForm}
          `,
        )
      }
    }
  }, [stripe, paymentIntent, isConnected, useCreditCardForm])

  const handleOtherAmountChange = async (value) => {
    let error = null
    const otherAmount = Number(value) * 100

    if (!value) {
      error = "Other amount can not be blank"
    } else if (otherAmount < 50) {
      error = `Other amount must be at least ${currency}0.50`
    } else if (
      otherAmount < reservation.minimumDue ||
      otherAmount > reservation.totalDue
    ) {
      error = "Other amount must be between minimum and full amount"
    }

    if (error) {
      setError(error)
      setCanMakePayment(false)
    } else {
      setError(null)
      await updatePaymentIntent(otherAmount)
      setAmount(otherAmount)
      setCanMakePayment(true)
    }
  }

  const handlePaymentTypeChange = async (value) => {
    setReducedOtherAmount(null)
    setError(null)

    if (value === "other") setCanMakePayment(false)
    let newAmount = null

    if (value === "full") {
      newAmount = reservation.totalDue
    } else if (value === "minimum") {
      newAmount = reservation.minimumDue
    }

    if ((value === "full" || value === "minimum") && newAmount < 50) {
      setError(`Amount must be at least ${currency}0.50`)
      setCanMakePayment(false)
      setPaymentType(value)
      return
    }
    setShowStripeForm(value !== "later")

    await updatePaymentIntent(newAmount)

    if (newAmount) setAmount(newAmount)
    setError(null)
    setPaymentType(value)
  }

  const handleDiscountRemove = async (discountSelection) => {
    setReducedOtherAmount(null)
    onIsProcessing(true)
    setCanMakePayment(false)

    const response = await onReservationSave(
      {
        data: { attributes: {} },
        included: [
          {
            type: "DiscountSelection",
            attributes: { id: discountSelection.id, _destroy: true },
          },
        ],
      },
      step.name,
    )

    if (
      paymentType === "full" &&
      response.data &&
      response.data.totalDue !== amount
    ) {
      await updatePaymentIntent(response.data.totalDue)
      setAmount(response.data.totalDue)
    } else {
      await updatePaymentIntent(amount)
      setAmount(amount)
    }
    onIsProcessing(false)
    setCanMakePayment(true)
  }

  const handleDiscountSave = async () => {
    onIsProcessing(true)
    setCanMakePayment(false)
    setDiscountError(null)

    const response = await onReservationSave(
      {
        data: { attributes: {} },
        included: [
          { type: "DiscountSelection", attributes: { code: discountCode } },
        ],
      },
      step.name,
    )

    if (!response.error) {
      setShowDiscounts(false)
    }

    if (
      paymentType === "full" &&
      response.data &&
      response.data.totalDue !== amount
    ) {
      await updatePaymentIntent(response.data.totalDue)
      setAmount(response.data.totalDue)
    } else if (
      paymentType !== "full" &&
      response.data &&
      response.data.totalDue < amount
    ) {
      setReducedOtherAmount(response.data.totalDue / 100)
      await updatePaymentIntent(response.data.totalDue)
      setAmount(response.data.totalDue)
      setReducedOtherAmount(null)
    } else {
      await updatePaymentIntent(amount)
      setAmount(amount)
    }
    onIsProcessing(false)
    setCanMakePayment(true)
  }

  const handleSave = async (stripeResponse = null) => {
    onIsProcessing(true)

    let attributes = {
      method: paymentMethod,
      type: paymentType,
    }

    if (paymentMethod === "creditCard" && paymentType !== "later") {
      attributes = {
        ...attributes,
        paymentIntentId: stripeResponse?.id,
        amount: stripeResponse?.amount,
        status: stripeResponse?.status,
      }
    }

    await onReservationSave({
      data: {
        attributes: {
          send_confirmation: true,
        },
      },
      included: [{ type: "Payment", attributes }],
    })

    return onIsProcessing(false)
  }

  const existingDiscountCodeSelection =
    _find(reservation.discountSelections, {
      kind: "code",
    }) || null

  const canApplyDiscount =
    !isProcessing && !disablePaymentForm && (!allowCreditCard || canMakePayment)

  const restrictedTo = discounts
    .filter((d) => d.kind === "code")
    .map((d) => d.restrictedTo)
    .flat()

  const showApplyDiscount = restrictedTo.some((r) =>
    attendees.map((a) => a.attendeeTypeId).includes(r),
  )

  const paymentFormOverlayStyles = {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }

  const mutatePaymentIntent = async (attributes) => {
    const response = await patchPaymentIntent(eventId, reservation.id, {
      data: { attributes: attributes },
      fields: {
        PaymentIntent:
          "amount,amount_formatted,client_secret,customer,currency,customer_session_client_secret,payment_method,payment_method_types,status",
      },
    })
    return response.data
  }

  const isReservationExpired = moment(reservation.expiresAt).isBefore(moment())

  const handleCanMakePayment = (canMakePayment) => {
    setCanMakePayment(canMakePayment)
    setError(null)
  }

  const handlePaymentOptionChange = (paymentOption) => {
    setPaymentMethod(paymentOption)
    setError(null)
    setShowStripeForm(paymentOption === "creditCard" && paymentType !== "later")
  }

  return (
    <div>
      <SectionLayout
        heading={hasTotalDue ? "Review and pay" : "Review"}
        headingId={headingId}
        className={className}
        step={step}
        expiresAt={reservation.expiresAt}
        isProcessing={isProcessing}
      >
        {step.isCurrent && (
          <>
            <AttendeesBreakdown
              attendees={attendees}
              addOns={addOns}
              isAddOnsEnabled={isAddOnsEnabled}
              hasTotalDue={hasTotalDue}
              totalDiscountString={reservation.totalDiscountString}
            />

            {hasTotalDue && (
              <div className="d-f ai-c jc-sb mb-3 mt-3">
                <strong>Total</strong>
                <strong>{reservation.totalDueString}</strong>
              </div>
            )}

            {showApplyDiscount && hasTotalDue && (
              <div className="mt-2 mb-1 d-f jc-fe">
                {existingDiscountCodeSelection ? (
                  <button
                    disabled={!canApplyDiscount}
                    className={`secondary-btn minor-btn btn destroy-btn`}
                    onClick={() =>
                      handleDiscountRemove(existingDiscountCodeSelection)
                    }
                  >
                    Remove code discount
                  </button>
                ) : (
                  <button
                    disabled={!canApplyDiscount}
                    className="secondary-btn minor-btn btn"
                    onClick={() => setShowDiscounts(!showDiscounts)}
                  >
                    Apply discount
                  </button>
                )}
              </div>
            )}

            {hasTotalDue && showDiscounts && (
              <ReviewPaymentDiscountForm
                error={discountError}
                discountCode={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                onSubmit={handleDiscountSave}
                isProcessing={isProcessing}
              />
            )}

            {usePaymentMethods && (
              <ReviewPaymentMethods
                onChange={handlePaymentOptionChange}
                paymentMethod={paymentMethod}
              />
            )}

            {usePaymentTypes && (
              <ReviewPaymentType
                onChange={handlePaymentTypeChange}
                onOtherAmountChange={handleOtherAmountChange}
                paymentType={paymentType}
                totalDueString={reservation.totalDueString}
                minimumDue={reservation.minimumDue}
                minimumDueString={reservation.minimumDueString}
                isPaymentUpdating={isPaymentProcessing}
                onCanMakePayment={setCanMakePayment}
                currencySymbol={currency}
                otherAmount={reducedOtherAmount}
              />
            )}

            {hasTotalDue && showStripeForm && (
              <StripeWrapper
                stripe={stripe}
                paymentIntent={paymentIntent}
                error={paymentIntentError || error}
                eventId={reservation.eventId}
                reservationId={reservation.id}
                onPaymentIntentUpdate={handlePaymentIntentUpdate}
                onCanMakePayment={handleCanMakePayment}
                canMakePayment={!disablePaymentForm && canMakePayment}
                mutatePaymentIntent={mutatePaymentIntent}
                allowedPaymentMethods={allowedPaymentMethods}
              >
                {isConnected && useCreditCardForm && (
                  <>
                    {!stripe || !paymentIntent?.clientSecret ? (
                      <Loading />
                    ) : (
                      <div
                        className={classNames("p-r", {
                          "mt-3": !showSavedPaymentMethods,
                        })}
                      >
                        {isCaptureProcessing && isProcessing && (
                          <div css={paymentFormOverlayStyles}>
                            <PaymentProcessingProgressBar
                              completeRegistrationId={completeRegistrationId}
                              onPaymentComplete={onPaymentComplete}
                            />
                          </div>
                        )}
                        <StripeForm
                          responsiblePerson={reservation.responsiblePerson}
                          paymentIntent={paymentIntent}
                          onPaymentSave={handleSave}
                          onIsProcessing={onIsProcessing}
                          onIsCaptureProcessing={setIsCaptureProcessing}
                          amount={amount}
                          onError={setError}
                          isTestMode={isTestMode}
                          isLoading={!stripe || !paymentIntent?.clientSecret}
                          reservationExpiresAt={reservation.expiresAt}
                        />
                      </div>
                    )}
                  </>
                )}
              </StripeWrapper>
            )}

            <AttendeesBreakdown attendees={attendees} forWaitlist />
          </>
        )}
        <div className="ta-c my-4">
          {useCreditCardForm && step.isCurrent && (
            <button
              className="btn"
              disabled={
                disablePaymentForm ||
                !canMakePayment ||
                isPaymentProcessing ||
                isProcessing ||
                isReservationExpired
              }
              form="paymentForm"
            >
              {`Register and pay ${
                amount
                  ? `${currency}${parseFloat(amount / 100).toFixed(2)}`
                  : ""
              }`}
            </button>
          )}

          {!useCreditCardForm && step.isCurrent && (
            <button
              className="btn"
              disabled={isProcessing || isReservationExpired}
              onClick={handleSave}
            >
              {isProcessing ? "Saving..." : "Register"}
            </button>
          )}
        </div>
      </SectionLayout>
    </div>
  )
}

ReviewPayment.propTypes = reservationStepPropTypes

export default ReviewPaymentWrapper
